import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor, brandConfig } from '../brandConfig';

export const CookiePolicy = () => {
  const { colorMode } = useColorMode();
  const lastUpdated = "November 19, 2025";

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            <VStack spacing={2} align="start" w="full">
              <Text fontSize="sm" color={getColor('text.secondary', colorMode)}>
                Last updated: {lastUpdated}
              </Text>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Cookie Policy
              </Heading>
            </VStack>

            <Divider />

            <VStack spacing={6} align="start" w="full">
              <Box>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  Our Website may use cookies to distinguish you from other users. This helps us provide you
                  with a good experience when you browse our Website and allows us to improve our Website.
                  By continuing to browse the Website, you are agreeing to our use of cookies as well as the
                  terms of this Cookie Policy.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  What Are Cookies?
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  A cookie is a small file of letters and numbers that we store on your browser or the hard
                  drive of your computer if you agree. Cookies contain information that is transferred to
                  your computer's hard drive.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Types of Cookies We Use
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                  We may use the following types of cookies:
                </Text>

                <VStack spacing={4} align="start" w="full">
                  <Box>
                    <Heading as="h3" size="md" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                      Strictly Necessary Cookies
                    </Heading>
                    <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                      These cookies are required for the operation of our Website. They include cookies that
                      enable you to log into secure areas, use a shopping cart, or make use of payment processing
                      services.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                      Analytical/Performance Cookies
                    </Heading>
                    <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                      These cookies allow us to recognize and count the number of visitors and see how visitors
                      move around our Website. This helps us improve the way our Website works, for example, by
                      ensuring that users are finding what they are looking for easily.
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                      Functionality Cookies
                    </Heading>
                    <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                      These cookies are used to recognize you when you return to our Website. This enables us to
                      personalize our content for you, greet you by name, and remember your preferences (for
                      example, your choice of language or region).
                    </Text>
                  </Box>

                  <Box>
                    <Heading as="h3" size="md" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                      Targeting Cookies
                    </Heading>
                    <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                      These cookies record your visit to our Website, the pages you have visited, and the links
                      you have followed. We will use this information to make our Website and any advertising
                      displayed more relevant to your interests.
                    </Text>
                  </Box>
                </VStack>
              </Box>

              <Box w="full">
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Cookies We Use
                </Heading>
                <Box overflowX="auto" w="full">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th color={getColor('text.secondary', colorMode)}>Cookie Name</Th>
                        <Th color={getColor('text.secondary', colorMode)}>Purpose</Th>
                        <Th color={getColor('text.secondary', colorMode)}>Duration</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td color={getColor('text.secondary', colorMode)}>session_id</Td>
                        <Td color={getColor('text.secondary', colorMode)}>
                          Used to maintain user session and authentication state
                        </Td>
                        <Td color={getColor('text.secondary', colorMode)}>Session</Td>
                      </Tr>
                      <Tr>
                        <Td color={getColor('text.secondary', colorMode)}>preferences</Td>
                        <Td color={getColor('text.secondary', colorMode)}>
                          Stores user preferences such as language and display settings
                        </Td>
                        <Td color={getColor('text.secondary', colorMode)}>1 year</Td>
                      </Tr>
                      <Tr>
                        <Td color={getColor('text.secondary', colorMode)}>analytics</Td>
                        <Td color={getColor('text.secondary', colorMode)}>
                          Used to collect anonymous usage statistics to improve our Website
                        </Td>
                        <Td color={getColor('text.secondary', colorMode)}>2 years</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Managing Cookies
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={3}>
                  You can control and/or delete cookies as you wish. You can delete all cookies that are already
                  on your computer and you can set most browsers to prevent them from being placed. However, if
                  you do this, you may have to manually adjust some preferences every time you visit our Website,
                  and some services and functionalities may not work.
                </Text>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  Most web browsers allow some control of cookies through browser settings. To find out more about
                  cookies, including how to see what cookies have been set and how to manage and delete them, visit
                  www.allaboutcookies.org.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Changes to This Cookie Policy
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for
                  other operational, legal, or regulatory reasons. Please revisit this Cookie Policy regularly
                  to stay informed about our use of cookies.
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Contact Us
                </Heading>
                <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                  If you have any questions about our use of cookies, please contact us at{' '}
                  {brandConfig.contact.email && (
                    <Text as="span" color={getColor('accent.primary', colorMode)} fontWeight="medium">
                      {brandConfig.contact.email}
                    </Text>
                  )}
                </Text>
              </Box>
            </VStack>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default CookiePolicy;
